import React, { useContext, useEffect, useRef, useState } from "react";
import {
    Modal,
    ModalHeader,
    ModalBody,
    Form,
    FormGroup,
    Label,
    Input,
    Button,
    Row,
    Col,
    Spinner,
} from "reactstrap";
import InputMask from "react-input-mask";
import { AuthContext } from "../../context/AuthContext";
import { InstituicaoContext } from "../../context/InstituicaoContext";
import { CreateInstituicaoType } from "../../context/hooks/useInstituicao";
import { CreateResponsavelType } from "../../context/hooks/useResponsavel";

interface Props {
    isOpen: boolean;
    toggle: () => void;
    onInstituicaoCriada: (instituicaoId: number) => void;
}

type ViaCepResponse = {
    cep?: string;
    logradouro?: string;
    complemento?: string;
    bairro?: string;
    localidade?: string;
    uf?: string;
    erro?: boolean;
};

export const CreateInstituicaoModal: React.FC<Props> = ({
    isOpen,
    toggle,
    onInstituicaoCriada,
}) => {
    const { objUserTk } = useContext(AuthContext);
    const { createInstituicao, getInstituicaoPaged } =
        useContext(InstituicaoContext);

    const [newInstituicao, setNewInstituicao] = useState<CreateInstituicaoType>(getDefaultInstituicao());
    const [newResponsavel, setNewResponsavel] = useState<CreateResponsavelType>(getDefaultResponsavel());
    const [responsaveis, setResponsaveis] = useState<CreateResponsavelType[]>([]);
    const [showResponsavelFields, setShowResponsavelFields] = useState(false);

    const [cepLoading, setCepLoading] = useState(false);
    const [cepError, setCepError] = useState<string | null>(null);
    const lastCepFetchedRef = useRef<string>("");
    const cepAbortRef = useRef<AbortController | null>(null);
    const cepDebounceRef = useRef<number | null>(null);

    function getDefaultInstituicao(): CreateInstituicaoType {
        return {
            name: "",
            email: "",
            state: "",
            city: "",
            neighborhood: "",
            address: "",
            number: 0,
            directorPhone: "",
            isPrivate: false,
            password: "",
            idEmpresa: objUserTk?.EmpresaId || 0,
            responsavel: [],
            CEP: "",
            pix: "",
        };
    }

    function getDefaultResponsavel(): CreateResponsavelType {
        return {
            name: "",
            email: "",
            CPF: "",
            telephone: "",
            position: "",
            bank: "",
            agency: "",
            currentAccount: "",
            observation: "",
            pix: "",
        };
    }

    const onlyDigits = (s?: string) => (s ?? "").replace(/\D/g, "");

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;

        if (name === "CEP") setCepError(null);

        setNewInstituicao((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const handleResponsavelInputChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        const { name, value, type, checked } = e.target;
        setNewResponsavel((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const handleAddResponsavel = () => {
        if (newResponsavel.name) {
            setResponsaveis([...responsaveis, newResponsavel]);
            setNewResponsavel(getDefaultResponsavel());
        }
    };

    const handleRemoveResponsavel = (index: number) => {
        const updated = [...responsaveis];
        updated.splice(index, 1);
        setResponsaveis(updated);
    };

    const resetFields = () => {
        setNewInstituicao(getDefaultInstituicao());
        setNewResponsavel(getDefaultResponsavel());
        setResponsaveis([]);
        setShowResponsavelFields(false);

        setCepLoading(false);
        setCepError(null);
        lastCepFetchedRef.current = "";

        if (cepAbortRef.current) {
            cepAbortRef.current.abort();
            cepAbortRef.current = null;
        }

        if (cepDebounceRef.current) {
            window.clearTimeout(cepDebounceRef.current);
            cepDebounceRef.current = null;
        }
    };

    useEffect(() => {
        if (isOpen) resetFields();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const applyViaCepToForm = (data: ViaCepResponse) => {
        // Não sobrescreve se o usuário já preencheu manualmente
        setNewInstituicao((prev) => ({
            ...prev,
            state: prev.state?.trim() ? prev.state : data.uf || "",
            city: prev.city?.trim() ? prev.city : data.localidade || "",
            neighborhood: prev.neighborhood?.trim()
                ? prev.neighborhood
                : data.bairro || "",
            address: prev.address?.trim() ? prev.address : data.logradouro || "",
        }));
    };

    const fetchViaCep = async (cepDigits: string) => {
        if (cepDigits.length !== 8) return;

        // evita repetir
        if (lastCepFetchedRef.current === cepDigits) return;

        // cancela a anterior
        if (cepAbortRef.current) cepAbortRef.current.abort();
        const controller = new AbortController();
        cepAbortRef.current = controller;

        setCepLoading(true);
        setCepError(null);

        try {
            const resp = await fetch(`https://viacep.com.br/ws/${cepDigits}/json/`, {
                method: "GET",
                signal: controller.signal,
            });

            if (!resp.ok) {
                throw new Error(`Falha ao consultar CEP (HTTP ${resp.status})`);
            }

            const json = (await resp.json()) as ViaCepResponse;

            if (json.erro) {
                setCepError("CEP não encontrado. Verifique e tente novamente.");
                return;
            }

            lastCepFetchedRef.current = cepDigits;
            applyViaCepToForm(json);
        } catch (err: any) {
            if (err?.name === "AbortError") return;
            setCepError("Não foi possível consultar o CEP agora. Tente novamente.");
        } finally {
            setCepLoading(false);
        }
    };

    // Auto-busca quando completar 8 dígitos (CEP preenchido)
    useEffect(() => {
        const cepDigits = onlyDigits(newInstituicao.CEP);

        // Se ainda não completou, não busca
        if (cepDigits.length !== 8) {
            setCepLoading(false);
            return;
        }

        if (cepDebounceRef.current) window.clearTimeout(cepDebounceRef.current);

        // debounce para evitar várias chamadas enquanto o usuário termina de digitar
        cepDebounceRef.current = window.setTimeout(() => {
            fetchViaCep(cepDigits);
        }, 350);

        return () => {
            if (cepDebounceRef.current) {
                window.clearTimeout(cepDebounceRef.current);
                cepDebounceRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newInstituicao.CEP]);

    // const handleSubmit = async (e: React.FormEvent) => {
    //     e.preventDefault();
    //     const data = { ...newInstituicao, responsavel: responsaveis };
    //     const createdId = await createInstituicao(data);

    //     if (createdId) {
    //         await getInstituicaoPaged(1, "", objUserTk.EmpresaId, 100);
    //         onInstituicaoCriada(Number(createdId));
    //         toggle();
    //     }
    // };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const sanitizedPayload: CreateInstituicaoType = {
            ...newInstituicao,
            name: newInstituicao.name?.trim() || "",
            email: newInstituicao.email?.trim() || "",
            city: newInstituicao.city?.trim() || "",
            state: newInstituicao.state?.trim().toUpperCase() || "",
            neighborhood: newInstituicao.neighborhood?.trim() || "",
            address: newInstituicao.address?.trim() || "",
            CEP: newInstituicao.CEP?.trim() || "",
            directorPhone: newInstituicao.directorPhone?.trim() || "",
            pix: newInstituicao.pix?.trim() || "",
            responsavel: responsaveis.map((r) => ({
                ...r,
                name: r.name?.trim() || "",
                email: r.email?.trim() || "",
                CPF: r.CPF?.trim() || "",
                telephone: r.telephone?.trim() || "",
                position: r.position?.trim() || "",
                bank: r.bank?.trim() || "",
                agency: r.agency?.trim() || "",
                currentAccount: r.currentAccount?.trim() || "",
                observation: r.observation?.trim() || "",
                pix: r.pix?.trim() || "",
            })),
        };

        const created = await createInstituicao(sanitizedPayload);
        if (!created) return;
        const createdId = (created as any)?.id;
        await getInstituicaoPaged(1, "", objUserTk.EmpresaId, 100);
        if (createdId) onInstituicaoCriada(Number(createdId));
        toggle();
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} backdrop="static" size="lg">
            <ModalHeader toggle={toggle}>Nova Instituição</ModalHeader>
            <ModalBody>
                <Form onSubmit={handleSubmit}>
                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label">Nome</Label>
                                <Input
                                    className="form-control-alternative"
                                    name="name"
                                    value={newInstituicao.name}
                                    onChange={handleInputChange}
                                    placeholder="Nome"
                                    required
                                />
                            </FormGroup>
                        </Col>

                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label">Email</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="email"
                                    name="email"
                                    value={newInstituicao.email}
                                    placeholder="Email"
                                    onChange={handleInputChange}
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="4">
                            <FormGroup>
                                <Label className="form-control-label d-flex align-items-center justify-content-between">
                                    <span>CEP</span>
                                    {cepLoading && (
                                        <span className="d-inline-flex align-items-center">
                                            <Spinner size="sm" />
                                            <span
                                                className="ml-2"
                                                style={{ fontSize: 12, opacity: 0.8 }}
                                            >
                                                Buscando...
                                            </span>
                                        </span>
                                    )}
                                </Label>

                                <InputMask
                                    className="form-control form-control-alternative"
                                    mask="99999-999"
                                    name="CEP"
                                    value={newInstituicao.CEP ?? ""}
                                    placeholder="00000-000"
                                    onChange={handleInputChange}
                                />

                                {cepError && (
                                    <div style={{ color: "#d9534f", fontSize: 12, marginTop: 6 }}>
                                        {cepError}
                                    </div>
                                )}
                            </FormGroup>
                        </Col>

                        <Col md="4">
                            <FormGroup>
                                <Label className="form-control-label">Estado (UF)</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="select"
                                    name="state"
                                    value={newInstituicao.state}
                                    onChange={handleInputChange}
                                >
                                    <option value="">Selecione o Estado</option>
                                    <option value="AC">AC - Acre</option>
                                    <option value="AL">AL - Alagoas</option>
                                    <option value="AP">AP - Amapá</option>
                                    <option value="AM">AM - Amazonas</option>
                                    <option value="BA">BA - Bahia</option>
                                    <option value="CE">CE - Ceará</option>
                                    <option value="DF">DF - Distrito Federal</option>
                                    <option value="ES">ES - Espírito Santo</option>
                                    <option value="GO">GO - Goiás</option>
                                    <option value="MA">MA - Maranhão</option>
                                    <option value="MT">MT - Mato Grosso</option>
                                    <option value="MS">MS - Mato Grosso do Sul</option>
                                    <option value="MG">MG - Minas Gerais</option>
                                    <option value="PA">PA - Pará</option>
                                    <option value="PB">PB - Paraíba</option>
                                    <option value="PR">PR - Paraná</option>
                                    <option value="PE">PE - Pernambuco</option>
                                    <option value="PI">PI - Piauí</option>
                                    <option value="RJ">RJ - Rio de Janeiro</option>
                                    <option value="RN">RN - Rio Grande do Norte</option>
                                    <option value="RS">RS - Rio Grande do Sul</option>
                                    <option value="RO">RO - Rondônia</option>
                                    <option value="RR">RR - Roraima</option>
                                    <option value="SC">SC - Santa Catarina</option>
                                    <option value="SP">SP - São Paulo</option>
                                    <option value="SE">SE - Sergipe</option>
                                    <option value="TO">TO - Tocantins</option>
                                </Input>
                            </FormGroup>
                        </Col>

                        <Col md="4">
                            <FormGroup>
                                <Label className="form-control-label">Cidade</Label>
                                <Input
                                    className="form-control-alternative"
                                    name="city"
                                    value={newInstituicao.city}
                                    placeholder="Cidade"
                                    onChange={handleInputChange}
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="4">
                            <FormGroup>
                                <Label className="form-control-label">Bairro</Label>
                                <Input
                                    className="form-control-alternative"
                                    name="neighborhood"
                                    value={newInstituicao.neighborhood}
                                    placeholder="Bairro"
                                    onChange={handleInputChange}
                                />
                            </FormGroup>
                        </Col>

                        <Col md="4">
                            <FormGroup>
                                <Label className="form-control-label">Endereço</Label>
                                <Input
                                    className="form-control-alternative"
                                    name="address"
                                    value={newInstituicao.address}
                                    placeholder="Endereço"
                                    onChange={handleInputChange}
                                />
                            </FormGroup>
                        </Col>

                        <Col md="4">
                            <FormGroup>
                                <Label className="form-control-label">Número</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="number"
                                    name="number"
                                    value={newInstituicao.number as any}
                                    placeholder="0"
                                    onChange={handleInputChange}
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="4">
                            <FormGroup>
                                <Label className="form-control-label">Telefone do diretor</Label>
                                <InputMask
                                    className="form-control form-control-alternative"
                                    mask="(99) 99999-9999"
                                    name="directorPhone"
                                    value={newInstituicao.directorPhone ?? ""}
                                    placeholder="(00)00000-0000"
                                    onChange={handleInputChange}
                                />
                            </FormGroup>
                        </Col>

                        <Col md="4">
                            <FormGroup>
                                <Label className="form-control-label">PIX</Label>
                                <Input
                                    className="form-control-alternative"
                                    name="pix"
                                    value={newInstituicao.pix}
                                    placeholder="PIX"
                                    onChange={handleInputChange}
                                />
                            </FormGroup>
                        </Col>

                        <Col md="4" className="d-flex align-items-center mt-4">
                            <FormGroup check className="d-flex align-items-center">
                                <Input
                                    className="form-check-input"
                                    type="checkbox"
                                    name="isPrivate"
                                    id="createIsPrivate"
                                    checked={newInstituicao.isPrivate || false}
                                    onChange={handleInputChange}
                                    style={{ cursor: "pointer", transform: "scale(1.2)", marginRight: "8px" }}
                                />
                                <Label check htmlFor="createIsPrivate" className="form-control-label mb-0" style={{ cursor: "pointer", fontWeight: 600 }}>
                                    Instituição Particular / Privada
                                </Label>
                            </FormGroup>
                        </Col>
                    </Row>

                    {/* Responsável */}
                    <Button
                        type="button"
                        color="link"
                        className="mb-3 text-primary p-0"
                        onClick={() => setShowResponsavelFields(!showResponsavelFields)}
                    >
                        {showResponsavelFields
                            ? "Ocultar campos do responsável"
                            : "Deseja vincular um responsável?"}
                    </Button>

                    {showResponsavelFields && (
                        <>
                            <Row>
                                <Col md="6">
                                    <Label className="form-control-label">Nome</Label>
                                    <Input
                                        className="form-control-alternative"
                                        name="name"
                                        value={newResponsavel.name}
                                        placeholder="Nome"
                                        onChange={handleResponsavelInputChange}
                                    />
                                </Col>
                                <Col md="6">
                                    <Label className="form-control-label">Email</Label>
                                    <Input
                                        className="form-control-alternative"
                                        name="email"
                                        value={newResponsavel.email}
                                        placeholder="Email"
                                        onChange={handleResponsavelInputChange}
                                    />
                                </Col>
                            </Row>

                            <Row>
                                <Col md="6">
                                    <Label className="form-control-label">CPF</Label>
                                    <InputMask
                                        className="form-control form-control-alternative"
                                        mask="999.999.999-99"
                                        name="CPF"
                                        value={newResponsavel.CPF ?? ""}
                                        placeholder="000.000.000-00"
                                        onChange={handleResponsavelInputChange}
                                    />
                                </Col>
                                <Col md="6">
                                    <Label className="form-control-label">Telefone</Label>
                                    <InputMask
                                        className="form-control form-control-alternative"
                                        mask="(99) 99999-9999"
                                        name="telephone"
                                        value={newResponsavel.telephone ?? ""}
                                        placeholder="(00) 00000-0000"
                                        onChange={handleResponsavelInputChange}
                                    />
                                </Col>
                            </Row>

                            <Row>
                                <Col md="6">
                                    <Label className="form-control-label">Cargo</Label>
                                    <Input
                                        className="form-control-alternative"
                                        name="position"
                                        value={newResponsavel.position}
                                        placeholder="Cargo"
                                        onChange={handleResponsavelInputChange}
                                    />
                                </Col>
                                <Col md="6">
                                    <Label className="form-control-label">PIX</Label>
                                    <Input
                                        className="form-control-alternative"
                                        name="pix"
                                        value={newResponsavel.pix}
                                        placeholder="PIX"
                                        onChange={handleResponsavelInputChange}
                                    />
                                </Col>
                            </Row>

                            <Row>
                                <Col md="4">
                                    <Label className="form-control-label">Banco</Label>
                                    <Input
                                        className="form-control-alternative"
                                        name="bank"
                                        value={newResponsavel.bank}
                                        placeholder="Nome do banco"
                                        onChange={handleResponsavelInputChange}
                                    />
                                </Col>
                                <Col md="4">
                                    <Label className="form-control-label">Agência</Label>
                                    <InputMask
                                        className="form-control form-control-alternative"
                                        mask="9999"
                                        name="agency"
                                        value={newResponsavel.agency ?? ""}
                                        placeholder="0000"
                                        onChange={handleResponsavelInputChange}
                                    />
                                </Col>
                                <Col md="4">
                                    <Label className="form-control-label">Conta</Label>
                                    <InputMask
                                        className="form-control form-control-alternative"
                                        mask="999999-9"
                                        name="currentAccount"
                                        value={newResponsavel.currentAccount ?? ""}
                                        placeholder="000000-0"
                                        onChange={handleResponsavelInputChange}
                                    />
                                </Col>
                            </Row>

                            <Row>
                                <Col>
                                    <Label className="form-control-label">Observação</Label>
                                    <Input
                                        className="form-control-alternative"
                                        type="textarea"
                                        name="observation"
                                        value={newResponsavel.observation}
                                        placeholder="Observação"
                                        onChange={handleResponsavelInputChange}
                                    />
                                </Col>
                            </Row>

                            <Row className="d-flex justify-content-end mt-2">
                                <Button
                                    type="button"
                                    style={{ color: "#FFF", backgroundColor: "#585858" }}
                                    className="btn btn-primary btn-sm mt-2"
                                    onClick={handleAddResponsavel}
                                    disabled={!newResponsavel.name}
                                >
                                    Adicionar responsável
                                </Button>
                            </Row>

                            <div className="mt-3">
                                {responsaveis.map((r, index) => (
                                    <span key={index} className="badge badge-primary mr-2">
                                        {r.name}
                                        <Button
                                            type="button"
                                            className="ml-1 btn btn-sm btn-danger"
                                            onClick={() => handleRemoveResponsavel(index)}
                                        >
                                            x
                                        </Button>
                                    </span>
                                ))}
                            </div>
                        </>
                    )}

                    <div className="d-flex justify-content-end mt-4">
                        <Button color="dark" type="submit">
                            Salvar instituição
                        </Button>
                        <Button color="danger" className="ml-2" onClick={toggle}>
                            Cancelar
                        </Button>
                    </div>
                </Form>
            </ModalBody>
        </Modal>
    );
};